import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { createRefetchContainer, graphql } from 'react-relay';
import { injectIntl, defineMessages } from 'react-intl';
import { updateUriRef } from '../../../actions/filterActions';
import { getUserSessionCountryCode } from 'dibs-buyer-layout/exports/regionalInfoHelpers';

// components
import { SbSharedRefineMenuTypeaheadControls } from './SbSharedRefineMenuTypeaheadControls';
import { SbSharedRefineMenuSearchInput } from '../SbSharedRefineMenuSearchInput/SbSharedRefineMenuSearchInput';
import { SbSharedRefineMenuSearchResults } from '../SbSharedRefineMenuSearchResults/SbSharedRefineMenuSearchResults';

// styles
import styles from './SbSharedRefineMenuTypeahead.scss';

// constants
const THRESHOLD_MIN_SEARCH_STRING_LENGTH = 3;
const THRESHOLD_FETCH_DATA_DEBOUNCE = 250;

const messages = defineMessages({
    searchBar: {
        id: 'sb.SbSharedRefineMenu.typeahead.searchBar',
        defaultMessage: `Search`,
    },
});

class SbSharedRefineMenuTypeaheadComponent extends React.Component {
    constructor() {
        super();

        this.state = {
            searchString: '',
            hideResults: true,
            isLoading: false,
        };

        this.handleInput = this.handleInput.bind(this);
        this.handleFetchResponse = this.handleFetchResponse.bind(this);
        this.handleResultClick = this.handleResultClick.bind(this);
        this.fetchSearchData = debounce(
            this.fetchSearchData.bind(this),
            THRESHOLD_FETCH_DATA_DEBOUNCE
        );
    }

    handleInput(e) {
        const { value: searchString } = e.target;

        if (searchString !== '' && searchString.length >= THRESHOLD_MIN_SEARCH_STRING_LENGTH) {
            this.fetchSearchData(searchString);
            this.setState({
                isLoading: true,
                searchString,
            });
        } else {
            this.setState({
                hideResults: true,
                searchString,
            });
        }
    }

    handleResultClick(filterValue) {
        this.props.dispatch(
            updateUriRef({
                filterName: this.props.filterName,
                filterValue,
                isFromTypeahead: true,
            })
        );
        this.props.onResultClick();
        /**
         * We intentionally clear the input box so that the user doesn't have to manually delete
         * any text to complete another search. Clearing the results list will also make it more
         * evident to the user that a new search can be entered.
         */
        this.setState({
            searchString: '',
        });
    }

    handleFetchResponse() {
        this.setState({
            isLoading: false,
            hideResults: this.state.searchString === '',
        });
    }

    fetchSearchData(value) {
        this.props.relay.refetch(
            {
                hasQuery: true,
                searchString: value,
                facetName: this.props.filterName,
                userCountryCode: getUserSessionCountryCode(),
            },
            null,
            this.handleFetchResponse
        );
    }

    render() {
        const { handleToggling, viewer, intl, filterName, excludedDisplayNames } = this.props;
        const { searchString, isLoading, hideResults } = this.state;
        const searchCopy = intl.formatMessage(messages.searchBar);

        return (
            <div className={styles.container}>
                <SbSharedRefineMenuSearchInput
                    className={styles.searchInput}
                    onSubmit={e => e.preventDefault()}
                    placeholder={searchCopy}
                    onInput={this.handleInput}
                    value={searchString}
                />
                <SbSharedRefineMenuSearchResults
                    itemFacetSearch={hideResults ? null : viewer.itemFacetSearch}
                    searchString={searchString}
                    isLoading={isLoading}
                    filterName={filterName}
                    excludedDisplayNames={excludedDisplayNames}
                    onResultClick={this.handleResultClick}
                />
                <SbSharedRefineMenuTypeaheadControls
                    showSearch={false}
                    onToggleClick={handleToggling}
                    filterName={filterName}
                />
            </div>
        );
    }
}

SbSharedRefineMenuTypeaheadComponent.propTypes = {
    handleToggling: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    onResultClick: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    filterName: PropTypes.string.isRequired,
    excludedDisplayNames: PropTypes.array.isRequired,
    intl: PropTypes.object,
    RefineMenuTypeaheadWrapper: PropTypes.element,
};

export const SbSharedRefineMenuTypeahead = createRefetchContainer(
    injectIntl(connect()(SbSharedRefineMenuTypeaheadComponent)),
    {
        viewer: graphql`
            fragment SbSharedRefineMenuTypeahead_viewer on Viewer
                @argumentDefinitions(
                    searchString: { type: "String" }
                    facetName: { type: "String" }
                    hasQuery: { type: "Boolean", defaultValue: false }
                    userCountryCode: { type: "String", defaultValue: "" }
                ) {
                itemFacetSearch(
                    searchString: $searchString
                    uriRef: $uriRef
                    facetName: $facetName
                    countryCode: $userCountryCode
                ) @include(if: $hasQuery) {
                    ...SbSharedRefineMenuSearchResults_itemFacetSearch
                }
            }
        `,
    },
    graphql`
        query SbSharedRefineMenuTypeaheadQuery(
            $searchString: String
            $hasQuery: Boolean!
            $facetName: String
            $uriRef: String
            $userCountryCode: String = ""
        ) {
            viewer {
                ...SbSharedRefineMenuTypeahead_viewer
                    @arguments(
                        searchString: $searchString
                        hasQuery: $hasQuery
                        facetName: $facetName
                        userCountryCode: $userCountryCode
                    )
            }
        }
    `
);
